import { assign, createMachine } from "xstate";
import {
  createOrchestrator,
  fetchOrchestrators,
  IFtthOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { createFtthOrder } from "@nju/data-access-portal-order";
import { BasicError, ContractError } from "@nju/result";
import { IScope } from "@nju/scope";

interface IContext {
  variant?: IScope;
  isLoggedIn: boolean;
  installationToken: string | null;
  selectedOfferId?: string;
  orchestrator?: IFtthOrchestrator;
  currentActiveCLient: string | null;
}

type IEvent =
  | { type: "SET_OFFER"; selectedOfferId: string }
  | {
      type: "DONE";
      orchestrator: IFtthOrchestrator;
    }
  | { type: "ERROR" }
  | {
      type: "PENDING_CANCELLATION";
      orchestrator: IFtthOrchestrator;
    }
  | { type: "ANOTHER_ORDER_EXISTS" };

export const selectOfferMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      isLoggedIn: false,
      // eslint-disable-next-line unicorn/no-null
      installationToken: null,
      // eslint-disable-next-line unicorn/no-null
      currentActiveCLient: null,
    },
    initial: "idle",
    states: {
      idle: {
        on: {
          SET_OFFER: {
            actions: "setOffer",
            target: "checkLoggedIn",
          },
        },
      },
      checkLoggedIn: {
        always: [
          {
            target: "checkOrchestrator",
            cond: "isLoggedInUser",
          },
          { target: "checkOrchestrator", cond: "isCurrentActiveCLient" },
          { target: "notLoggedIn" },
        ],
      },
      checkOrchestrator: {
        invoke: {
          src: "fetchOrchestrator",
        },
        on: {
          ERROR: "error",
          PENDING_CANCELLATION: "debounceCheckOrchestrator",
          DONE: {
            actions: "setFtthOrchestrator",
            target: "validateOrder",
          },
        },
      },
      debounceCheckOrchestrator: {
        after: {
          2000: {
            target: "checkOrchestrator",
          },
        },
      },
      validateOrder: {
        always: [
          { target: "newOrder", cond: "isNewOrder" },
          { target: "inProgress" },
        ],
      },
      newOrder: {
        invoke: {
          src: "createOrder",
        },
        on: {
          ANOTHER_ORDER_EXISTS: {
            actions: "redirectToAnotherOrderExists",
          },
          ERROR: "error",
          DONE: {
            actions: ["setFtthOrchestrator", "onSuccessRedirect"],
            type: "final",
          },
        },
      },
      notLoggedIn: {
        entry: "onRegisterPageRedirect",
        type: "final",
      },
      inProgress: {
        type: "final",
        entry: "onInProgress",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setOffer: assign((context, event) => {
        if (event.type === "SET_OFFER") {
          return {
            selectedOfferId: event.selectedOfferId,
          };
        }

        return context;
      }),
      setFtthOrchestrator: assign((context, event) => {
        if (event.type === "DONE") {
          return {
            orchestrator: event.orchestrator,
          };
        }

        return context;
      }),
    },
    guards: {
      isLoggedInUser: (ctx) => {
        return ctx.isLoggedIn;
      },
      isNewOrder: (ctx) => {
        return !ctx.orchestrator?.orderId && !!ctx.installationToken;
      },
      isCurrentActiveCLient: (ctx) => {
        return ctx.variant === "sales" && !!ctx.currentActiveCLient;
      },
    },
    services: {
      fetchOrchestrator: () => {
        return async (callback) => {
          try {
            const orchestrators = (await fetchOrchestrators()).unwrap();

            if (!orchestrators.ftth) {
              const orchestrator = (
                await createOrchestrator("NJU_FTTH_ONBOARDING", {})
              ).unwrap();

              if (!orchestrator) {
                throw new BasicError({
                  message: "Missing ftth orchestrator",
                });
              }

              return callback({ type: "DONE", orchestrator });
            }

            if (orchestrators.ftth.state === "ORDER_PENDING_CANCELLATION") {
              return callback({
                type: "PENDING_CANCELLATION",
                orchestrator: orchestrators.ftth,
              });
            }

            return callback({
              type: "DONE",
              orchestrator: orchestrators.ftth,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      createOrder: ({ installationToken, selectedOfferId, orchestrator }) => {
        return async (callback) => {
          try {
            if (installationToken === null) {
              throw new ContractError({
                message: "`installationToken` cannot be null",
              });
            }

            if (!orchestrator?.id) {
              throw new ContractError({
                message: "`orchestrator.id` is missing",
              });
            }

            if (!selectedOfferId) {
              throw new ContractError({
                message: "`selectedOfferId` is missing",
              });
            }

            const result = await createFtthOrder({
              mode: "ftth",
              installationToken,
              offerIds: [selectedOfferId],
              orchestratorId: orchestrator.id,
            });

            if (
              result.isError &&
              result.err.code === "ANOTHER_ORDER_IN_PROGRESS"
            ) {
              return callback({ type: "ANOTHER_ORDER_EXISTS" });
            }

            if (result.isError) {
              return callback({ type: "ERROR" });
            }

            return callback({
              type: "DONE",
              orchestrator: result.value,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
    },
  },
);
